import { Address, Org, OrgsApi, PartnerProduct, ProductType, User } from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import { AddressService } from '@/services/address-service';
import { OrgExtended } from '@/models/org';

export class OrgService {
	constructor(
		private orgApi: OrgsApi = new OrgsApi(),
		private addressService: AddressService = new AddressService(),
	) {}

	public async getOrgById<T extends Org>(
		id: string | number,
		includes: string[],
		params: { [key: string]: string },
	): Promise<{ org?: T; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			const response = await this.orgApi.getOrgById(id, undefined, {
				params: {
					include: includes,
					...params,
				},
			});

			const extendedOrg = <OrgExtended>response.data;
			extendedOrg.address = this.addressService.convertAddressToObject(
				<Address>extendedOrg.address,
			);
			return { org: <T>extendedOrg };
		} catch (e) {
			console.error('Error getting org by ID:', e);
			return ApiUtils.handleApiError(e, `Failed to fetch org by ID: ${id}`);
		}
	}

	public async getOrgPartnerProducts(
		id: string | number,
	): Promise<{ products?: PartnerProduct[]; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			const response = await this.orgApi.getOrgPartnerProducts(id);
			return { products: response.data.data?.filter(p => p !== null) ?? [] };
		} catch (e) {
			console.error('Error getting org partner products by ID:', e);
			return ApiUtils.handleApiError(e, `Failed to fetch org partner products by ID: ${id}`);
		}
	}

	public async getOrgUsers(
		id: string | number,
	): Promise<{ users?: User[]; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			const response = await this.orgApi.getOrgUsers(id);
			return { users: response.data.data ?? [] };
		} catch (e) {
			console.error('Error getting org users by ID:', e);
			return ApiUtils.handleApiError(e, `Failed to fetch org users by ID: ${id}`);
		}
	}

	public async acceptTerms(
		id: string | number,
	): Promise<{ success?: boolean; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			await this.orgApi.acceptTerms(id);
			return { success: true };
		} catch (e) {
			console.error('Error accepting terms for org:', e);
			return ApiUtils.handleApiError(e, `Failed to accept terms for org by ID: ${id}`);
		}
	}

	public async deleteOrg(
		id: string | number,
	): Promise<{ success?: boolean; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			await this.orgApi.deleteOrg(id);
			return { success: true };
		} catch (e) {
			console.error('Error deleting org by ID:', e);
			return ApiUtils.handleApiError(e, `Failed to delete org by ID: ${id}`);
		}
	}

	public async orgInstallJob(
		id: string | number,
	): Promise<{ success?: boolean; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			await this.orgApi.orgInstallJob(id);
			return { success: true };
		} catch (e) {
			console.error('Error creating install job for org by ID:', e);
			return ApiUtils.handleApiError(e, `Failed to create install job for org by ID: ${id}`);
		}
	}

	public async orgGetBillingPartnersByProductType(
		productType: ProductType,
	): Promise<{ orgs?: Org[]; errorMessage?: string }> {
		try {
			const response = await this.orgApi.orgGetBillingPartnersByProductType(productType);
			return { orgs: response.data.data };
		} catch (e) {
			console.error('Error getting org partners by product type:', e);
			return ApiUtils.handleApiError(
				e,
				`Failed to fetch org partners by product type: ${productType}`,
			);
		}
	}
}
